import { useCallback, useRef } from 'react';

/**
 * QA-409 — Daum(카카오) 우편번호 검색. FE 회원가입(storefront useDaumPostcode)과 같은 공개 스크립트·같은 정규화라
 * 관리자마다 "서울 / 서울시 / 서울특별시"처럼 표기가 갈리지 않는다. API 키가 필요 없다.
 * 스크립트는 첫 호출 때 한 번만 불러온다.
 */
export const DAUM_POSTCODE_SRC = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

export interface DaumPostcodeResult {
  readonly zonecode: string;
  readonly roadAddress: string;
  readonly jibunAddress: string;
  readonly userSelectedType: 'R' | 'J';
  readonly buildingName: string;
  readonly apartment: 'Y' | 'N';
}

export interface DaumPostcodeAddress {
  readonly zipcode: string;
  /** 선택 타입에 맞춘 기본 주소(도로명 우선). */
  readonly address: string;
}

interface DaumNamespace {
  Postcode: new (options: {
    oncomplete: (data: DaumPostcodeResult) => void;
    onclose?: (state: 'FORCE_CLOSE' | 'COMPLETE_CLOSE') => void;
  }) => { open: () => void };
}

declare global {
  interface Window {
    daum?: DaumNamespace;
  }
}

const loadScript = (): Promise<DaumNamespace> =>
  new Promise((resolve, reject) => {
    if (window.daum?.Postcode) {
      resolve(window.daum);
      return;
    }
    const done = () => (window.daum?.Postcode ? resolve(window.daum) : reject(new Error('Daum Postcode failed to initialize')));
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${DAUM_POSTCODE_SRC}"]`);
    const script = existing ?? document.createElement('script');
    script.addEventListener('load', done);
    script.addEventListener('error', () => reject(new Error('Daum Postcode script error')));
    if (!existing) {
      script.src = DAUM_POSTCODE_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
  });

export const buildDaumAddress = (data: DaumPostcodeResult): DaumPostcodeAddress => {
  const base = data.userSelectedType === 'J' ? data.jibunAddress : data.roadAddress;
  const withBuilding =
    data.userSelectedType === 'R' && data.buildingName && data.apartment === 'Y' ? `${base} (${data.buildingName})` : base;
  return { zipcode: data.zonecode, address: withBuilding };
};

export const useDaumPostcode = (onSelect: (address: DaumPostcodeAddress) => void, onError?: () => void) => {
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const openingRef = useRef(false);

  const open = useCallback(async (): Promise<void> => {
    if (openingRef.current) return;
    openingRef.current = true;
    try {
      const daum = await loadScript();
      new daum.Postcode({
        oncomplete: (data) => onSelectRef.current(buildDaumAddress(data)),
        onclose: () => {
          openingRef.current = false;
        },
      }).open();
    } catch {
      openingRef.current = false;
      onErrorRef.current?.();
    }
  }, []);

  return { open };
};
